import { useState, useEffect, useRef } from 'react';
import { Swords, Bot, Users, Mic, PenTool, Check, ArrowRight, Clock, Loader2, X, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import MuziCoach from '../components/MuziCoach';
import DeviceTesterModal from '../components/DeviceTesterModal';
import { BATTLE_CARDS_VOCAL, BATTLE_CARDS_SILENCE } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { aiApi, battlesApi } from '../lib/api';
import { createBattleSocket } from '../lib/battleSocket';

export default function Battle() {
  const { user, requireAuth, updateUser } = useAuth();
  const [phase, setPhase] = useState('select'); // select | playing | result | queue | online_play | online_end
  const [mode, setMode] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [card, setCard] = useState(null);
  const [timer, setTimer] = useState(30);
  const [myScore, setMyScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [round, setRound] = useState(1);
  const [input, setInput] = useState('');
  const [coachReaction, setCoachReaction] = useState(null);
  const [validating, setValidating] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [saving, setSaving] = useState(false);
  // Online state
  const [onlineOpponent, setOnlineOpponent] = useState(null); // { user_id, username, avatar }
  const [onlineResult, setOnlineResult] = useState(null); // 'win' | 'loss' | 'draw'
  const [onlineMsg, setOnlineMsg] = useState(null); // round info
  const [answerSent, setAnswerSent] = useState(false);

  const timerRef = useRef(null);
  const sessionRef = useRef(`battle_${Date.now()}`);
  const handleTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const wsHandlerRef = useRef(null);

  // Timer countdown for vs IA (uses ref to call the latest handleTimeout)
  useEffect(() => {
    if (phase !== 'playing') return undefined;
    if (timer <= 0) {
      handleTimeoutRef.current?.();
      return undefined;
    }
    timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timer, phase]);

  // Timer countdown for online play (visual only — server is authoritative)
  useEffect(() => {
    if (phase !== 'online_play') return undefined;
    if (timer <= 0) return undefined;
    const id = setTimeout(() => setTimer((t) => Math.max(0, t - 1)), 1000);
    return () => clearTimeout(id);
  }, [timer, phase]);

  // Cleanup WS on unmount
  useEffect(() => () => { wsRef.current?.close(); }, []);

  const fetchCoachReaction = async (ctx) => {
    try {
      const r = await aiApi.coachReaction({ context: ctx, coach_id: 'c1', session_id: sessionRef.current });
      setCoachReaction(r.reaction);
    } catch {
      setCoachReaction(null);
    }
  };

  const handleTimeout = () => {
    setAiScore((s) => s + 1);
    fetchCoachReaction('timeout');
    nextRound();
  };
  handleTimeoutRef.current = handleTimeout;

  // ===== ONLINE FLOW =====
  const startOnline = (m) => {
    const token = localStorage.getItem('muzi_token');
    if (!token) { toast.error('Connecte-toi d\'abord'); return; }
    setPhase('queue');
    setMyScore(0); setAiScore(0); setRound(1);
    setOnlineOpponent(null); setOnlineResult(null); setOnlineMsg(null);
    setInput(''); setCard(null);

    wsRef.current = createBattleSocket(token, {
      onOpen: () => { wsRef.current?.joinQueue(m); },
      onMessage: (msg) => wsHandlerRef.current?.(msg, m),
      onClose: () => {
        if (phase === 'queue' || phase === 'online_play') {
          toast.error('Connexion perdue');
        }
      },
    });
  };

  const handleWsMessage = (msg, m) => {
    switch (msg.type) {
      case 'queued':
        toast.info(`En recherche d'un adversaire... (place #${msg.position})`);
        break;
      case 'matched':
        setOnlineOpponent(msg.opponent);
        toast.success(`Match trouvé vs ${msg.opponent.username} ${msg.opponent.avatar}`);
        break;
      case 'round_start':
        setPhase('online_play');
        setRound(msg.round);
        setCard(msg.card);
        setTimer(msg.timer);
        setInput('');
        setAnswerSent(false);
        setOnlineMsg(null);
        break;
      case 'answer_result':
        if (msg.valid) toast.success('✅ ' + (msg.reason || 'Validé !'));
        else { toast.error('❌ ' + (msg.reason || 'Invalide')); setAnswerSent(false); }
        break;
      case 'round_end': {
        const scores = msg.scores || {};
        const myId = user?.id;
        const oppId = onlineOpponent?.user_id || Object.keys(scores).find((k) => k !== myId);
        setMyScore(scores[myId] || 0);
        setAiScore(oppId ? (scores[oppId] || 0) : 0);
        if (msg.winner === myId) setOnlineMsg('🏆 Round remporté !');
        else if (msg.winner) setOnlineMsg('😤 Round perdu');
        else setOnlineMsg('— Égalité sur ce round');
        break;
      }
      case 'battle_end':
        setPhase('online_end');
        setMyScore(msg.my_score);
        setAiScore(msg.opponent_score);
        setOnlineResult(msg.result);
        if (user) {
          updateUser({
            coins: (user.coins || 0) + msg.coins_earned,
            xp: (user.xp || 0) + msg.xp_earned,
            battles_total: (user.battles_total || 0) + 1,
            battles_won: (user.battles_won || 0) + (msg.result === 'win' ? 1 : 0),
          });
        }
        toast.success(`+${msg.xp_earned} XP • +${msg.coins_earned} crédits`);
        wsRef.current?.close();
        break;
      case 'opponent_disconnect':
        toast.error('Adversaire déconnecté');
        setPhase('select');
        wsRef.current?.close();
        break;
      case 'error':
        toast.error(msg.message || 'Erreur');
        break;
      default:
        break;
    }
  };
  // Keep WS handler ref fresh to avoid stale closures
  wsHandlerRef.current = handleWsMessage;

  const cancelQueue = () => {
    wsRef.current?.leaveQueue();
    wsRef.current?.close();
    setPhase('select');
  };

  const sendOnlineAnswer = () => {
    if (answerSent) return;
    setAnswerSent(true);
    if (mode === 'silence') {
      if (!input.trim()) { setAnswerSent(false); return; }
      wsRef.current?.answer(input.trim());
    } else {
      wsRef.current?.answer('');
    }
  };

  const sendOnlineSkip = () => {
    if (answerSent) return;
    setAnswerSent(true);
    wsRef.current?.skip();
  };

  // ===== vs IA FLOW =====
  const startBattle = (m, o) => {
    if (!requireAuth('register')) { toast.info('Crée un compte pour commencer la battle 🎤'); return; }
    setMode(m); setOpponent(o);
    if (o === 'online') { startOnline(m); return; }
    setMyScore(0); setAiScore(0); setRound(1);
    sessionRef.current = `battle_${Date.now()}_${user?.id || 'guest'}`;
    drawCard(m);
    setPhase('playing');
    fetchCoachReaction('start_battle');
  };

  const drawCard = (m) => {
    const pool = m === 'vocal' ? BATTLE_CARDS_VOCAL : BATTLE_CARDS_SILENCE;
    setCard(pool[Math.floor(Math.random() * pool.length)]);
    setTimer(30); setInput('');
  };

  const validate = async () => {
    if (mode === 'silence' && card) {
      setValidating(true);
      try {
        const r = await aiApi.validateWord({ target_letters: card.target, word: input.trim(), category: card.type, session_id: sessionRef.current });
        if (r.valid) { setMyScore((s) => s + 1); setCoachReaction(r.coach_reaction); toast.success('✅ ' + r.reason); }
        else { setAiScore((s) => s + 1); setCoachReaction(r.coach_reaction); toast.error('❌ ' + r.reason); }
      } catch { toast.error('Erreur de validation IA'); }
      finally { setValidating(false); }
    } else {
      setMyScore((s) => s + 1);
      await fetchCoachReaction('good_sing');
      toast.success('🎤 Super perf !');
    }
    nextRound();
  };

  const skipCard = () => {
    setAiScore((s) => s + 1);
    fetchCoachReaction('lose_round');
    nextRound();
  };

  const nextRound = () => {
    if (round >= 5) {
      setTimeout(() => finalize(), 700);
    } else {
      setTimeout(() => { setRound((r) => r + 1); drawCard(mode); }, 800);
    }
  };

  const finalize = async () => {
    setSaving(true);
    setPhase('result');
    try {
      const battle = await battlesApi.save({
        mode, opponent,
        opponent_name: opponent === 'ai' ? 'MUZI IA' : 'Adversaire',
        my_score: myScore, opponent_score: aiScore, rounds: 5,
      });
      const newCoins = (user?.coins || 0) + battle.coins_earned;
      const newXp = (user?.xp || 0) + battle.xp_earned;
      updateUser({ coins: newCoins, xp: newXp, battles_total: (user?.battles_total || 0) + 1, battles_won: (user?.battles_won || 0) + (battle.result === 'win' ? 1 : 0) });
      toast.success(`+${battle.xp_earned} XP • +${battle.coins_earned} crédits`);
      fetchCoachReaction(battle.result === 'win' ? 'win_battle' : 'lose_battle');
    } catch { toast.error('Sauvegarde battle échouée'); }
    finally { setSaving(false); }
  };

  const reset = () => {
    wsRef.current?.close();
    setPhase('select'); setMode(null); setOpponent(null);
    setMyScore(0); setAiScore(0); setRound(1);
    setCoachReaction(null); setOnlineOpponent(null); setOnlineResult(null);
  };

  // ====== SELECT PHASE ======
  if (phase === 'select') {
    return (
      <div className="relative pt-24 pb-12 min-h-screen">
        <DeviceTesterModal open={showDeviceModal} onOpenChange={setShowDeviceModal} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-purple-500/15 text-purple-300 border-0 mb-3 px-3 py-1 rounded-full"><Swords size={12} className="mr-1" /> Mode Battle</Badge>
            <h1 className="text-4xl sm:text-5xl font-black mb-2">Choisis ton <span className="text-gradient">terrain</span></h1>
            <p className="text-zinc-400 mb-3">5 rounds, le meilleur gagne. Prêt(e) ?</p>
            <button
              onClick={() => setShowDeviceModal(true)}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-pink-300 glass-light px-3 py-1.5 rounded-full"
              data-testid="battle-test-devices-btn"
            >
              <SettingsIcon size={12} /> Tester caméra & micro avant de jouer
            </button>
          </div>

          <div className="mb-8">
            <h2 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">1. Adversaire</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <button onClick={() => setOpponent('ai')} className={`glass rounded-2xl p-6 text-left card-hover border-2 ${opponent === 'ai' ? 'border-pink-500' : 'border-transparent'}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-3">
                  <Bot size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-black">vs IA</h3>
                <p className="text-sm text-zinc-400">Entraîne-toi 24/7. Réactions GPT en direct.</p>
                <Badge className="mt-3 bg-green-500/20 text-green-300 border-0 text-xs">Disponible</Badge>
              </button>
              <button onClick={() => setOpponent('online')} className={`glass rounded-2xl p-6 text-left card-hover border-2 ${opponent === 'online' ? 'border-pink-500' : 'border-transparent'}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-3">
                  <Users size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-black">vs En ligne</h3>
                <p className="text-sm text-zinc-400">Multi-joueur temps réel. Premier validé gagne le round.</p>
                <Badge className="mt-3 bg-pink-500/20 text-pink-300 border-0 text-xs flex items-center gap-1 w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" /> LIVE
                </Badge>
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">2. Mode de jeu</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <button onClick={() => setMode('vocal')} className={`glass rounded-2xl p-6 text-left card-hover border-2 ${mode === 'vocal' ? 'border-pink-500' : 'border-transparent'}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-3">
                  <Mic size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-black">Mode Vocal</h3>
                <p className="text-sm text-zinc-400">Chante. En ligne : premier à cliquer &quot;J&apos;ai chanté&quot; gagne le round.</p>
              </button>
              <button onClick={() => setMode('silence')} className={`glass rounded-2xl p-6 text-left card-hover border-2 ${mode === 'silence' ? 'border-pink-500' : 'border-transparent'}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-3">
                  <PenTool size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-black">Mode Silence</h3>
                <p className="text-sm text-zinc-400">Trouve un mot français avec les lettres. <strong className="text-pink-400">Validation IA stricte.</strong></p>
              </button>
            </div>
          </div>

          <Button onClick={() => mode && opponent && startBattle(mode, opponent)} disabled={!mode || !opponent} className="w-full h-14 btn-neon text-white border-0 rounded-2xl text-lg font-black disabled:opacity-40">
            <Swords size={20} className="mr-2" /> LANCER LA BATTLE
          </Button>
        </div>
      </div>
    );
  }

  // ====== QUEUE (waiting for opponent) ======
  if (phase === 'queue') {
    return (
      <div className="relative pt-24 pb-12 min-h-screen flex items-center">
        <div className="max-w-md mx-auto px-4 text-center w-full">
          <div className="glass rounded-3xl p-10">
            <Loader2 className="mx-auto animate-spin text-pink-400 mb-4" size={48} />
            <h2 className="text-2xl font-black mb-2">Recherche d&apos;un adversaire...</h2>
            <p className="text-zinc-400 mb-1">Mode <span className="text-pink-400 font-bold capitalize">{mode}</span></p>
            <p className="text-xs text-zinc-500 mb-6">Connecte un ami avec son code MUZ00X pour matcher plus vite</p>
            <Button onClick={cancelQueue} variant="outline" className="h-11 px-5 rounded-xl bg-white/5 hover:bg-white/10 text-white border-white/15 font-bold">
              <X size={16} className="mr-1.5" /> Annuler
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ====== ONLINE END ======
  if (phase === 'online_end') {
    const win = onlineResult === 'win';
    const draw = onlineResult === 'draw';
    return (
      <div className="relative pt-24 pb-12 min-h-screen flex items-center">
        <div className="max-w-2xl mx-auto px-4 text-center w-full">
          <div className="glass rounded-3xl p-10">
            <div className="text-8xl mb-4 bounce-soft">{win ? '🏆' : draw ? '🤝' : '💪'}</div>
            <h1 className="text-5xl font-black mb-2">
              {win ? <span className="text-gradient">VICTOIRE !</span> : draw ? <span className="text-yellow-300">Égalité</span> : <span className="text-zinc-300">Belle bataille</span>}
            </h1>
            <p className="text-zinc-400 mb-6">vs {onlineOpponent?.username || 'Adversaire'}</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="glass-light rounded-2xl p-5">
                <div className="text-4xl mb-1">{user?.avatar || '✨'}</div>
                <div className="text-xs text-zinc-400">{user?.username || 'Toi'}</div>
                <div className="text-3xl font-black text-pink-400">{myScore}</div>
              </div>
              <div className="glass-light rounded-2xl p-5">
                <div className="text-4xl mb-1">{onlineOpponent?.avatar || '🎤'}</div>
                <div className="text-xs text-zinc-400">{onlineOpponent?.username || 'Adversaire'}</div>
                <div className="text-3xl font-black text-purple-400">{aiScore}</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => startBattle(mode, 'online')} className="flex-1 btn-neon h-12 rounded-xl text-white border-0 font-bold">
                <Swords size={16} className="mr-1.5" /> Rejouer en ligne
              </Button>
              <Button onClick={reset} variant="outline" className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 text-white border-white/15 font-bold">
                Retour
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ====== RESULT (vs AI) ======
  if (phase === 'result') {
    const win = myScore > aiScore;
    return (
      <div className="relative pt-24 pb-12 min-h-screen flex items-center">
        <div className="max-w-2xl mx-auto px-4 text-center w-full">
          <div className="glass rounded-3xl p-10">
            {saving ? <Loader2 className="mx-auto animate-spin text-pink-400 mb-4" size={40} /> : null}
            <div className="text-8xl mb-4 bounce-soft">{win ? '🏆' : '💪'}</div>
            <h1 className="text-5xl font-black mb-2">
              {win ? <span className="text-gradient">VICTOIRE !</span> : <span className="text-zinc-300">Belle bataille</span>}
            </h1>
            <p className="text-zinc-400 mb-6">{win ? 'Tu domines ce terrain.' : 'La prochaine sera la bonne.'}</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="glass-light rounded-2xl p-5">
                <div className="text-4xl mb-1">{user?.avatar || '✨'}</div>
                <div className="text-xs text-zinc-400">{user?.username || 'Toi'}</div>
                <div className="text-3xl font-black text-pink-400">{myScore}</div>
              </div>
              <div className="glass-light rounded-2xl p-5">
                <div className="text-4xl mb-1">🤖</div>
                <div className="text-xs text-zinc-400">MUZI IA</div>
                <div className="text-3xl font-black text-purple-400">{aiScore}</div>
              </div>
            </div>
            {coachReaction && <div className="mb-6"><MuziCoach coachId="c1" reaction={coachReaction} size="md" /></div>}
            <div className="flex gap-3">
              <Button onClick={() => startBattle(mode, opponent)} className="flex-1 btn-neon h-12 rounded-xl text-white border-0 font-bold">
                <Swords size={16} className="mr-1.5" /> Revanche
              </Button>
              <Button onClick={reset} variant="outline" className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 text-white border-white/15 font-bold">
                Retour
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ====== PLAYING (vs IA) AND ONLINE_PLAY share the same UI ======
  const isOnline = phase === 'online_play';
  const opponentDisplay = isOnline
    ? { avatar: onlineOpponent?.avatar || '🎤', name: onlineOpponent?.username || 'Adversaire' }
    : { avatar: '🤖', name: 'MUZI IA' };

  return (
    <div className="relative pt-24 pb-12 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-2xl p-4 mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-2xl">{user?.avatar || '✨'}</div>
              <div className="text-xl font-black text-pink-400">{myScore}</div>
            </div>
            <div className="text-zinc-500 font-bold text-lg">VS</div>
            <div className="text-center">
              <div className="text-2xl">{opponentDisplay.avatar}</div>
              <div className="text-xl font-black text-purple-400">{aiScore}</div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-zinc-400">Round</div>
            <div className="text-xl font-black text-white">{round}/5</div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5">
            <Clock size={16} className={timer <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'} />
            <span className={`text-xl font-black ${timer <= 10 ? 'text-red-400' : 'text-white'}`}>{timer}s</span>
          </div>
        </div>

        {isOnline && onlineMsg && (
          <div className="glass-light rounded-xl p-3 mb-4 text-center text-sm font-bold text-pink-300">{onlineMsg}</div>
        )}

        {!isOnline && (
          <div className="mb-5"><MuziCoach coachId="c1" reaction={coachReaction} size="md" /></div>
        )}

        {card && (
          <div className="rounded-3xl p-10 mb-5 relative overflow-hidden text-center border-2" style={{ background: `linear-gradient(135deg, ${card.color}40, ${card.color}10)`, borderColor: `${card.color}60` }}>
            <div className="text-7xl mb-4 bounce-soft">{card.emoji}</div>
            {mode === 'vocal' ? (
              <>
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-300 mb-2">{card.category}</div>
                <div className="text-6xl font-black text-white mb-4">{card.word}</div>
                <p className="text-sm text-zinc-300">{card.hint}</p>
              </>
            ) : (
              <>
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-300 mb-2">{card.type}</div>
                <div className="text-5xl font-black text-white mb-3 tracking-widest">{card.target}</div>
                <p className="text-sm text-zinc-300 mb-4">{card.hint}</p>
                {card.examples && <div className="text-xs text-zinc-400"><span className="font-semibold">Exemples :</span> {card.examples.join(' • ')}</div>}
              </>
            )}
          </div>
        )}

        {mode === 'silence' ? (
          <div className="glass rounded-2xl p-4 mb-3">
            <Input autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ton mot ici..."
              className="bg-white/5 border-white/10 text-white text-lg h-12 rounded-xl"
              onKeyDown={(e) => e.key === 'Enter' && !validating && !answerSent && (isOnline ? sendOnlineAnswer() : validate())}
              disabled={validating || answerSent}
            />
          </div>
        ) : (
          <div className="glass rounded-2xl p-4 mb-3 text-center">
            <div className="text-sm text-zinc-300 mb-2">🎤 Chante maintenant !</div>
            <div className="flex justify-center gap-1">
              {[1,2,3,4,5,6,7,8].map((i) => (
                <div key={`bar-${i}`} className="w-1.5 rounded-full bg-pink-500" style={{ height: `${20 + (i * 4) % 30}px`, animation: `bounce-soft ${0.5 + i * 0.05}s ease-in-out infinite` }} />
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={isOnline ? sendOnlineAnswer : validate} disabled={validating || (isOnline && answerSent)} className="flex-1 btn-neon h-12 rounded-xl text-white border-0 font-bold">
            {validating || (isOnline && answerSent) ? <Loader2 size={18} className="animate-spin mr-1.5" /> : <Check size={18} className="mr-1.5" />}
            {validating ? 'Validation IA...' : answerSent ? 'Attente adversaire...' : mode === 'vocal' ? "J'ai chanté !" : 'Valider'}
          </Button>
          <Button onClick={isOnline ? sendOnlineSkip : skipCard} disabled={isOnline && answerSent} variant="outline" className="h-12 px-5 rounded-xl bg-white/5 hover:bg-white/10 text-white border-white/15 font-bold">
            <ArrowRight size={18} className="mr-1.5" /> Passer
          </Button>
        </div>
      </div>
    </div>
  );
}
